import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RefinerService } from '@app/services/refiner.services';
import { userPreferencesStore } from '@app/signal-store/user-preferences/user-preferences.store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { mgcIconFlaskRegular } from '@sb/mgc-icons/mgcIcon-flask_regular.icon';
import { mgcIconHotkeyRegular } from '@sb/mgc-icons/mgcIcon-hotkey_regular.icon';
import { mgcIconLayoutLeftbarCloseRegular } from '@sb/mgc-icons/mgcIcon-layout_leftbar_close_regular.icon';
import { mgcIconReportFormsRegular } from '@sb/mgc-icons/mgcIcon-report_forms_regular.icon';
import { mgcIconWallet4Regular } from '@sb/mgc-icons/mgcIcon-wallet_4_regular.icon';
import { FeatureService, type FeatureFlag } from '@sb/shared/feature';
import {
  DialogConfirmType,
  SbDialogContentDirective,
  SbDialogHeaderComponent,
  SbDialogService,
  SbDialogTitleDirective,
} from '@sb/ui/dialog';
import { IconRegistry } from '@sb/ui/icon';
import { InfoCardComponent } from '@sb/ui/info-card';
import { SurfaceDirective } from '@sb/ui/surface';
import { SwitchComponent } from '@sb/ui/switch';
import { filter } from 'rxjs';

import { labsItemList, resolveLabsItemDescription } from './labs-dialog.items';

@Component({
  selector: 'labs-dialog',
  templateUrl: './labs-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SbDialogHeaderComponent,
    SbDialogTitleDirective,
    TranslatePipe,
    SbDialogContentDirective,
    SwitchComponent,
    InfoCardComponent,
    SurfaceDirective,
  ],
})
export class LabsDialogComponent {
  userPreferencesStore = inject(userPreferencesStore);

  #featureService = inject(FeatureService);
  #refinerService = inject(RefinerService);
  #dialogService = inject(SbDialogService);
  #translationService = inject(TranslateService);
  #destroyRef = inject(DestroyRef);
  #wasLabsUser = this.userPreferencesStore.labs_enabled();

  #isFlagEnabled = (flag: FeatureFlag) => this.#featureService.isEnabled(flag);

  // A card is shown when at least one of its modal flags is on; its copy is then
  // resolved from whichever of those flags are enabled.
  visibleLabsItems = labsItemList
    .filter((labsItem) => labsItem.modalFeatureFlags.some(this.#isFlagEnabled))
    .map((labsItem) => ({
      ...labsItem,
      description: resolveLabsItemDescription(labsItem, this.#isFlagEnabled),
    }));

  constructor() {
    inject(IconRegistry).registerIcons([
      mgcIconFlaskRegular,
      mgcIconLayoutLeftbarCloseRegular,
      mgcIconHotkeyRegular,
      mgcIconReportFormsRegular,
      mgcIconWallet4Regular,
    ]);

    effect((onCleanup) => {
      onCleanup(() => {
        if (this.#wasLabsUser && !this.userPreferencesStore.labs_enabled()) {
          this.#refinerService.showSurvey('web-labs-feedback');
        }
      });
    });
  }

  openFeedbackSurvey(surveyId: string) {
    this.#refinerService.showSurvey(surveyId);
  }

  optInToggle() {
    if (this.userPreferencesStore.labs_enabled()) {
      const dialogConfirmRef = this.#dialogService.openConfirm({
        type: DialogConfirmType.warning,
        title: this.#translationService.instant('Toggle OFF all Labs features?'),
        description: this.#translationService.instant('Are you sure you want to toggle OFF all Labs features?'),
        primary: {
          text: this.#translationService.instant('Yes, toggle off'),
        },
      });

      dialogConfirmRef.closed
        .pipe(
          filter((payload) => payload?.confirmed),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe(() => {
          // The store shows the confirmation toast on success.
          this.userPreferencesStore.setLabsEnabled(false);
        });
    } else {
      this.userPreferencesStore.setLabsEnabled(true);
    }
  }
}
